import { useState, useEffect } from 'react';
import axios from 'axios';
import { Building2, Key, Calendar, ChevronRight, RefreshCw, Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MyClients({ user, onLogout, onSelectClient }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/my-clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data);
    } catch (err) {
      console.error('Error fetching clients:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredClients = clients.filter(client => 
    client.firm_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'bg-green-100 text-green-700';
      case 'In Setup': return 'bg-yellow-100 text-yellow-700';
      case 'Inactive': return 'bg-slate-100 text-slate-600';
      default: return 'bg-green-100 text-green-700';
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-slate-900 text-white px-8 py-4 flex items-center justify-between" data-testid="my-clients-header">
        <div>
          <h1 className="text-xl font-bold" style={{fontFamily: 'Outfit'}}>Law Agency 360</h1>
          <p className="text-xs text-slate-400 mt-1">Internal Team Portal</p>
        </div>
        <div className="flex items-center gap-6">
          <div className="text-right">
            <div className="text-sm font-medium">{user.email}</div>
            <div className="text-xs text-[#4338CA]">Internal Team</div>
          </div>
          <button
            onClick={onLogout}
            className="px-4 py-2 text-sm bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors"
            data-testid="logout-btn"
          >
            Logout
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto p-8">
        {/* Page Title */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-slate-900" style={{fontFamily: 'Outfit'}}>
            My Assigned Clients
          </h2>
          <p className="text-slate-600 mt-1">Select a client to view and manage their dashboard</p>
        </div>

        {/* Search and Refresh */}
        <div className="flex items-center gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search clients..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4338CA] bg-white"
              data-testid="search-clients"
            />
          </div>
          <button
            onClick={fetchClients}
            className="flex items-center gap-2 px-4 py-3 text-slate-600 hover:text-slate-900 border border-slate-200 rounded-lg bg-white transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>

        {/* Clients Count */}
        <div className="mb-6 text-sm text-slate-500">
          Showing {filteredClients.length} of {clients.length} clients
        </div>

        {loading ? (
          <div className="text-center py-12 text-slate-600">Loading clients...</div>
        ) : filteredClients.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
            <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No clients assigned</h3>
            <p className="text-slate-500">
              {searchTerm ? 'No clients match your search' : 'Contact your administrator to get clients assigned'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="clients-grid">
            {filteredClients.map((client) => (
              <div
                key={client.id}
                className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-lg hover:border-[#4338CA]/30 transition-all cursor-pointer group"
                onClick={() => onSelectClient(client.firm_id, client.firm_name)}
                data-testid={`client-card-${client.id}`}
              >
                {/* Client Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-[#4338CA]/10 rounded-lg flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-[#4338CA]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900" style={{fontFamily: 'Outfit'}}>
                        {client.firm_name}
                      </h3>
                      <p className="text-sm text-slate-500">{client.email}</p>
                    </div>
                  </div>
                </div>

                {/* Status and Plan */}
                <div className="flex items-center gap-2 mb-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(client.status)}`}>
                    {client.status || 'Active'}
                  </span>
                  <span className="px-2 py-1 bg-[#4338CA]/10 text-[#4338CA] rounded-full text-xs font-medium">
                    {client.subscription_plan}
                  </span>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="flex items-center gap-2 text-sm">
                    <Key className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-600">
                      <span className="font-medium text-slate-900">{client.keywords_count}</span> keywords
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-600">
                      {client.last_updated 
                        ? new Date(client.last_updated).toLocaleDateString() 
                        : 'No updates'}
                    </span>
                  </div>
                </div>

                {/* Open Dashboard Button */}
                <button
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-slate-50 group-hover:bg-[#4338CA] text-slate-600 group-hover:text-white rounded-lg font-medium transition-colors"
                  data-testid={`open-dashboard-${client.id}`}
                >
                  Open Dashboard
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
